import React from 'react';
import { Grid, Card, CardHeader, Typography, Autocomplete, TextField } from '@mui/material';
import LoadingScreen from 'src/frontend-common/components/LoadingScreen';
import { CustomWidthTooltip } from 'src/style/common';
import { featureImportanceTooltip } from 'src/pages/FeatureAnalysis/utils';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SpecialGridWrapper } from 'src/pages/FeatureAnalysis/style';
import ErrorBoundary from 'src/frontend-common/components/MicroServiceErrorBundary';
import ChartWrapper from 'src/frontend-common/components/Charts/Wrapper';
import { colors } from 'src/frontend-common/utils/palette';
import { IAlertAnalytic } from 'src/frontend-common/dto/Interfaces/Analytics';
import jsonParse from 'src/frontend-common/typesafe/jsonParse';

type Entry = Record<string, number>;
interface Props {
  dataAnalysis: any;
  verticalBarChartData: IAlertAnalytic[] | undefined;
  product: string | undefined;
  productLabels: string[] | undefined;
  setActiveProduct: React.Dispatch<React.SetStateAction<string | undefined>>;
  featureDistributionPerClass:
    | {
        true?: { ko: number; ok: number };
        false?: { ko: number; ok: number };
      }
    | undefined;
  setFeatureDistributionPerClass: React.Dispatch<
    React.SetStateAction<
      | {
          true?:
            | {
                ko: number;
                ok: number;
              }
            | undefined;
          false?:
            | {
                ko: number;
                ok: number;
              }
            | undefined;
        }
      | undefined
    >
  >;
  koDataArray: undefined;
  setKoDataArray: React.Dispatch<React.SetStateAction<undefined>>;
  featureName: string | undefined;
  featureNames: string[] | undefined;
  setFeatureName: React.Dispatch<React.SetStateAction<string | undefined>>;
  setFeatureNames: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  a11yProps: (index: number) => {
    id: string;
    'aria-controls': string;
  };
}

const FeatureImportance: React.FC<Props> = ({
  dataAnalysis,
  verticalBarChartData,
  product,
  productLabels,
  setActiveProduct,
  featureDistributionPerClass,
  setFeatureDistributionPerClass,
  setKoDataArray,
  featureName,
  featureNames,
  setFeatureName,
  setFeatureNames,
}) => {
  const [data, setData] = React.useState<{ ok: Record<number, Entry>; ok_line: number; ko_line: number; ko: Record<number, Entry> }>();
  const [featureDistributionPerClassArray, setFeatureDistributionPerClassArray] = React.useState<
    Record<
      string,
      {
        true?: { ko: number; ok: number };
        false?: { ko: number; ok: number };
      }
    >
  >();

  const Alerts = React.lazy(() => import('FrontendAnalytics/Alerts'));

  const hasAtLeastOneDefinedValue = (data: {
    ok: Record<number, Entry> | undefined;
    ok_line: number | undefined;
    ko_line: number | undefined;
    ko: Record<number, Entry> | undefined;
  }): boolean => {
    return data.ok !== undefined || data.ko !== undefined || data.ok_line !== undefined || data.ko_line !== undefined;
  };

  React.useEffect(() => {
    if (product) {
      const parsedFeatureProductMapping = jsonParse(dataAnalysis.data?.item?.feature_product_mapping);

      if (parsedFeatureProductMapping && typeof parsedFeatureProductMapping === 'object') {
        const key = product === 'IP Address' ? 'ip' : product === 'Multiple Products' ? 'cross' : product?.toLowerCase();
        const featureNames = (parsedFeatureProductMapping as Record<string, string[]>)[key as string] || [];
        if (product === 'Multiple Products') {
          setFeatureNames([...featureNames]);
        } else {
          setFeatureNames(featureNames);
        }
        setFeatureName(featureNames[0]);
      }
    }
  }, [product]);

  const getPercentageData = (
    ok_ko_percentageData: any,
    featureImportanceData: {
      name: string;
      pv: number;
    }[]
  ) => {
    if (Object.keys(ok_ko_percentageData).length === 0) return [];

    const dataKoPercentage = Object.fromEntries(
      Object.entries(ok_ko_percentageData[featureName || featureImportanceData[0]?.name || ''])?.map(([key, value]) => {
        const v = value as { percentage: number };
        return [key, { percentage: v.percentage }];
      })
    );

    const firstEntry = Object.values(ok_ko_percentageData[featureName || featureImportanceData[0]?.name || ''])[0] as Entry[];

    const lineLabelKey = Object.keys(firstEntry).find((k) => k.includes('_line'));

    const averageLabel = lineLabelKey ? (firstEntry as any)[lineLabelKey] : undefined;

    return [dataKoPercentage, averageLabel];
  };
  React.useEffect(() => {
    if (dataAnalysis.data?.item?.value) {
      const parsedData = JSON.parse(dataAnalysis.data?.item?.value);
      const featureImportanceData: { name: string; pv: number }[] = parsedData['feature_importance']?.data;

      const koPercentageData = parsedData[`ko_percentage_per_value`];
      const okPercentageData = parsedData[`ok_percentage_per_value`];

      setKoDataArray(koPercentageData);

      const koData = getPercentageData(koPercentageData, featureImportanceData);
      const okData = getPercentageData(okPercentageData, featureImportanceData);

      setData({ ok: okData[0], ok_line: okData[1], ko: koData[0], ko_line: koData[1] });
    }
  }, [featureName]);
  React.useEffect(() => {
    if (dataAnalysis.data?.item?.value) {
      const parsedData = JSON.parse(dataAnalysis.data?.item?.value);
      const featureDistributionClass: Record<
        string,
        {
          true?: { ko: number; ok: number };
          false?: { ko: number; ok: number };
        }
      > = parsedData['feature_distribution_per_class'];

      setFeatureDistributionPerClass(featureDistributionClass[featureName || '']);

      setFeatureDistributionPerClassArray(featureDistributionClass);
    }
  }, [featureName, dataAnalysis]);

  React.useEffect(() => {
    if (dataAnalysis.data?.item?.value) {
      const parsedData = JSON.parse(dataAnalysis.data?.item?.value);
      const featureDistributionClass: Record<
        string,
        {
          true?: { ko: number; ok: number };
          false?: { ko: number; ok: number };
        }
      > = parsedData['feature_distribution_per_class'];

      setFeatureDistributionPerClass(featureDistributionClass[featureName || '']);

      setFeatureDistributionPerClassArray(featureDistributionClass);
    }
  }, [featureName, dataAnalysis]);

  return (
    <>
      <Grid item xs={12}>
        <Card style={{ paddingRight: '15px' }}>
          <CardHeader
            style={{ paddingLeft: 17 }}
            title="Feature Importance"
            action={
              <div
                style={{
                  float: 'right',
                }}
              >
                <CustomWidthTooltip title={featureImportanceTooltip}>
                  <InfoOutlinedIcon />
                </CustomWidthTooltip>
              </div>
            }
          />
          <ErrorBoundary>
            <React.Suspense fallback={<LoadingScreen variant="circle" />}>
              <Alerts
                data={{ items: verticalBarChartData }}
                isAlertsLoading={false}
                isAlertsError={false}
                isRiskChart
                itemsPerPage={7}
                itemsPagination={true}
                showEmptyState={false}
              />
            </React.Suspense>
          </ErrorBoundary>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5" style={{ marginTop: 20, marginBottom: 20 }}>
          Individual Feature Analysis
        </Typography>
        {verticalBarChartData && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                disablePortal
                id="product_input"
                value={product}
                options={productLabels || []}
                onChange={(e, newInputValue) => {
                  if (newInputValue) {
                    setActiveProduct(newInputValue);
                    setFeatureName(undefined);
                    setFeatureDistributionPerClass(featureDistributionPerClassArray?.[newInputValue]);
                  }
                }}
                renderInput={(params) => <TextField label="Product" {...params} />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                disablePortal
                id="feature_input"
                value={featureName}
                options={featureNames || []}
                onChange={(e, newInputValue) => {
                  if (newInputValue) {
                    setFeatureName(newInputValue);
                    setFeatureDistributionPerClass(featureDistributionPerClassArray?.[newInputValue]);
                  }
                }}
                renderInput={(params) => <TextField {...params} label="Feature name" />}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
      <SpecialGridWrapper item xs={12}>
        <Grid container spacing={3}>
          {featureDistributionPerClass && (
            <Grid item xs={12} md={6}>
              <ErrorBoundary>
                <ChartWrapper
                  components={[
                    {
                      chart: {
                        data: Object.entries(featureDistributionPerClass)?.map(([key, value]) => ({
                          xAxisLabel: key,
                          values: value,
                        })),
                        barSize: 22,
                        palette: [colors.error.main, colors.success.main],
                        type: 'barChart',
                        stackedBars: false,
                        cartesianGrid: { horizontalLines: true, verticalLines: false },
                        tooltipValueFormatter: (x: number) => x.toString(),
                        tooltipFooterValueFormatter: (x: number) => x.toString(),
                        height: 600,
                        tooltipFooter: 'max',
                      },
                      tabLabel: '',
                      title: 'Feature Distribution',
                    },
                  ]}
                />
              </ErrorBoundary>
            </Grid>
          )}
          {data && hasAtLeastOneDefinedValue(data) && (
            <Grid item xs={12} md={6}>
              <ChartWrapper
                components={[
                  {
                    chart: {
                      data:
                        data.ok &&
                        Object.entries(data.ok)?.map(([key, value]) => ({
                          xAxisLabel: key,
                          values: value,
                        })),
                      barSize: 22,
                      referenceLineValue: data.ok_line,
                      type: 'barChart',
                      stackedBars: false,
                      cartesianGrid: { horizontalLines: true, verticalLines: false },
                      tooltipValueFormatter: (x: number) => x.toString(),
                      tooltipFooterValueFormatter: (x: number) => x.toString(),
                      height: 600,
                      tooltipFooter: 'max',
                    },

                    tabLabel: 'OK',
                    title: 'Feature OK Distribution',
                  },
                  {
                    chart: {
                      data:
                        data.ko &&
                        Object.entries(data.ko)?.map(([key, value]) => ({
                          xAxisLabel: key,
                          values: value,
                        })),
                      barSize: 22,
                      referenceLineValue: data.ko_line,
                      type: 'barChart',
                      stackedBars: false,
                      cartesianGrid: { horizontalLines: true, verticalLines: false },
                      tooltipValueFormatter: (x: number) => x.toString(),
                      tooltipFooterValueFormatter: (x: number) => x.toString(),
                      height: 600,
                      tooltipFooter: 'max',
                    },

                    tabLabel: 'KO',
                    title: 'Feature KO Distribution',
                  },
                ]}
              />
            </Grid>
          )}
        </Grid>
      </SpecialGridWrapper>
    </>
  );
};

export default FeatureImportance;
